/**
 * Saidiseire's desk: every customer site we watch, whether it is up, and what
 * needs a look.
 *
 * Top to bottom: the engine strip (is anything being checked at all), the four
 * counts, the search and the state chips, the table (down first), and under it
 * what nobody watches yet: the sites on our own platform (item K), then the
 * clients' websites.
 *
 * The counts come from the server's summary (`deskCounts`): the server counts
 * a site never seen working apart from the confirmed outages, so it sits under
 * "unclear" and the down tile says exactly what the bell's "sites down" says.
 * The chips count the loaded rows, because they filter the loaded rows.
 *
 * Switching the engine off while sites are down asks first and then says how
 * many outages it closed: those outages end there, with no "back up" alert,
 * and a red row simply turning grey reads as a recovery otherwise.
 *
 * Every action re-reads the list after it answers. The row itself is replaced
 * at once from the action's answer, but the tiles are the server's, and a
 * pause that closed an outage has to take it off the down tile too.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowDownCircle, ArrowUpCircle, CircleHelp, Loader2, Plus, RotateCcw } from "lucide-react";
import {
  MONITOR_FILTERS,
  deskCounts,
  filterCounts,
  filterMonitors,
  monitorErrorKey,
  saveMonitorSettings,
  sortMonitors,
  type MonitorFilter,
  type MonitorRow,
} from "../../../lib/monitoring";
import { unwatched, type PlatformSiteOption } from "../../../lib/monitoringPlatform";
import { Btn, Kpi, PageHead, PillTabs, SearchField, Toolbar, ToolbarGap, fmtNum, useToast } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { EngineStrip } from "./EngineStrip";
import { MonitorDialog } from "./MonitorDialog";
import { MonitorsTable } from "./MonitorsTable";
import { PlatformSitesCard, usePlatformSites } from "./platform";
import { SettingsDialog } from "./SettingsDialog";
import { SuggestionsCard } from "./SuggestionsCard";
import { useMonitorActions } from "./useMonitorActions";
import { useMonitors } from "./useMonitors";
import type { T } from "./words";

/** A clock for "is the engine late", ticking with the poll so the strip turns
 *  amber on an open page without anything else changing. */
function useNow(stepMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), stepMs);
    return () => clearInterval(timer);
  }, [stepMs]);
  return now;
}

export function MonitoringDesk({ canManage, t }: { canManage: boolean; t: T }) {
  const navigate = useNavigate();
  const toast = useToast();
  const now = useNow();
  const desk = useMonitors({ withSuggestions: canManage });
  const platform = usePlatformSites(canManage);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<MonitorFilter>("all");
  const [dialog, setDialog] = useState<{ site: MonitorRow | null; preset?: PlatformSiteOption } | null>(null);
  const [settings, setSettings] = useState(false);
  const [removing, setRemoving] = useState<MonitorRow | null>(null);
  const [savingEngine, setSavingEngine] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const actions = useMonitorActions({
    onChanged: (site) => {
      desk.replaceRow(site);
      void desk.reload();
    },
    onRemoved: () => {
      void desk.reload();
      // A removed platform site is on offer again.
      platform.reload();
    },
  });

  const all = desk.data?.sites ?? [];
  const rows = sortMonitors(filterMonitors(all, q, filter));
  const counts = filterCounts(filterMonitors(all, q, "all"));
  const summary = desk.data ? deskCounts(desk.data.summary) : null;
  const downNow = summary?.down ?? 0;

  async function toggleEngine(enabled: boolean) {
    setSavingEngine(true);
    try {
      await saveMonitorSettings({ enabled });
      toast(
        enabled
          ? t("monitoring.toast.engineOn")
          : downNow > 0
            ? t("monitoring.toast.engineOffClosed", { n: downNow })
            : t("monitoring.toast.engineOff"),
      );
    } catch (err) {
      toast(t(`monitoring.error.${monitorErrorKey(err)}`));
    } finally {
      setSavingEngine(false);
      await desk.reload();
    }
  }

  const addButton = canManage ? (
    <Btn tone="dark" onClick={() => setDialog({ site: null })}>
      <Plus className="size-4" />
      {t("monitoring.add")}
    </Btn>
  ) : null;

  const searching = q.trim() !== "" || filter !== "all";

  return (
    <div className="crm-page">
      <PageHead title={t("monitoring.title")} sub={t("monitoring.sub")} actions={addButton} />

      {desk.data ? (
        <EngineStrip
          engine={desk.data.engine}
          canManage={canManage}
          saving={savingEngine}
          now={now}
          t={t}
          onToggle={(on) => (!on && downNow > 0 ? setConfirmOff(true) : void toggleEngine(on))}
          onSettings={() => setSettings(true)}
        />
      ) : null}

      {summary ? (
        // Two across below 900px. `.crm-kpis` only folds inside the overview's
        // `.crm-dash` container query, so on any other page four tiles stayed
        // four at 375px, each too narrow for its own label.
        <div className="crm-kpis max-[900px]:grid-cols-2">
          {/* Four tiles, the grid's width. Paused is a footnote of the total
              rather than a fifth tile: it is the one count nobody acts on. */}
          <Kpi
            icon={Activity}
            label={t("monitoring.kpi.total")}
            value={fmtNum(summary.total)}
            foot={summary.paused > 0 ? t("monitoring.kpi.pausedFoot", { n: summary.paused }) : undefined}
          />
          <Kpi icon={ArrowUpCircle} label={t("monitoring.kpi.up")} value={fmtNum(summary.up)} tone="success" />
          <Kpi
            icon={ArrowDownCircle}
            label={t("monitoring.kpi.down")}
            value={fmtNum(summary.down)}
            tone={summary.down > 0 ? "destructive" : "primary"}
          />
          <Kpi
            icon={CircleHelp}
            label={t("monitoring.kpi.other")}
            value={fmtNum(summary.other)}
            tone="warning"
            foot={
              summary.neverUp > 0
                ? t("monitoring.kpi.otherFootNever", {
                    blocked: summary.blocked,
                    unknown: summary.unknown,
                    never: summary.neverUp,
                  })
                : t("monitoring.kpi.otherFoot", { blocked: summary.blocked, unknown: summary.unknown })
            }
          />
        </div>
      ) : null}

      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("monitoring.search")} />
        <PillTabs
          small
          value={filter}
          onChange={setFilter}
          tabs={MONITOR_FILTERS.map((f) => ({
            value: f,
            label: t(`monitoring.filter.${f}`),
            count: counts[f] > 0 ? counts[f] : undefined,
          }))}
        />
        <ToolbarGap />
        {searching ? (
          <Btn
            tone="plain"
            small
            onClick={() => {
              setQ("");
              setFilter("all");
            }}
          >
            <RotateCcw className="size-[15px]" strokeWidth={1.8} />
            {t("monitoring.reset")}
          </Btn>
        ) : null}
      </Toolbar>

      {desk.failed && !desk.data ? (
        <div className="crm-card py-10 text-center text-[13px]">
          <p className="mb-3 text-[var(--color-destructive)]">{t("monitoring.loadFailed")}</p>
          <Btn small onClick={() => void desk.reload()}>
            {t("monitoring.retry")}
          </Btn>
        </div>
      ) : desk.loading ? (
        <div className="crm-card flex justify-center py-16 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : (
        <MonitorsTable
          rows={rows}
          canManage={canManage}
          engineOn={desk.data?.engine.enabled ?? false}
          busyId={actions.busyId}
          t={t}
          empty={
            all.length === 0
              ? { title: t("monitoring.empty.title"), body: t("monitoring.empty.body"), action: addButton }
              : { title: t("monitoring.empty.filteredTitle"), body: t("monitoring.empty.filteredBody") }
          }
          onCheck={(site) => void actions.checkNow(site)}
          onPause={(site, paused) => void actions.setPaused(site, paused)}
          onEdit={(site) => setDialog({ site })}
          onRemove={setRemoving}
        />
      )}

      {canManage ? (
        <PlatformSitesCard sites={unwatched(platform.sites ?? [])} t={t} onAdd={(preset) => setDialog({ site: null, preset })} />
      ) : null}
      {canManage ? (
        <SuggestionsCard
          suggestions={desk.suggestions}
          t={t}
          onAdded={() => {
            void desk.reload();
            // A client's website can be one of our own platform's addresses.
            platform.reload();
          }}
        />
      ) : null}

      {dialog ? (
        <MonitorDialog
          site={dialog.site}
          platformSites={platform.sites}
          preset={dialog.preset ?? null}
          t={t}
          onClose={() => setDialog(null)}
          onSaved={(site) => {
            if (dialog.site) desk.replaceRow(site);
            else {
              void desk.reload();
              platform.reload();
            }
          }}
          onOpen={(id) => navigate(`/monitoring/${id}`)}
        />
      ) : null}
      {settings ? (
        <SettingsDialog t={t} onClose={() => setSettings(false)} onSaved={() => void desk.reload()} />
      ) : null}
      {confirmOff ? (
        <ConfirmModal
          title={t("monitoring.engine.offConfirmTitle")}
          body={t("monitoring.engine.offConfirmBody", { n: downNow })}
          confirmLabel={t("monitoring.engine.offConfirmAction")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await toggleEngine(false);
            setConfirmOff(false);
          }}
          onClose={() => setConfirmOff(false)}
        />
      ) : null}
      {removing ? (
        <ConfirmModal
          title={t("monitoring.remove.title")}
          body={t("monitoring.remove.body", { host: removing.host })}
          confirmLabel={t("monitoring.menu.remove")}
          cancelLabel={t("common.cancel")}
          onConfirm={async () => {
            await actions.remove(removing);
            setRemoving(null);
          }}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </div>
  );
}
